import type { AxiosInstance } from 'axios';
import type { Paginated } from '@/types';
import type { AffiliateBalance, AffiliatePlan, AffiliateSummary, AffiliateTransaction, KioskAffiliate } from '@/types/affiliates';
import baseAxios from '@/helpers/forms/axios';

/**
 * URIs for the write endpoints, exposed so components can hand them to
 * `sendForm()` and inherit its busy/successful handling and interceptor-driven
 * 422 reporting. Mirrors the `impersonateUrl` helper on the kiosk module.
 */
export const affiliateUris = {
    addAffiliate: '/affiliates-spark/kiosk/affiliates/add',
    addAffiliatePlan: '/affiliates-spark/kiosk/affiliates/plans/add',
    withdraw: '/api/affiliates/withdraw',
};

interface AffiliatesApi {
    fetchKioskAffiliates: () => Promise<KioskAffiliate[]>
    fetchAffiliatePlans: () => Promise<AffiliatePlan[]>
    fetchSummary: () => Promise<AffiliateSummary>
    fetchTransactions: (page: number) => Promise<Paginated<AffiliateTransaction>>
    fetchBalance: () => Promise<AffiliateBalance>
}

export default (axios: AxiosInstance = baseAxios): AffiliatesApi => ({
    fetchKioskAffiliates: async (): Promise<KioskAffiliate[]> => {
        const response = await axios.get<KioskAffiliate[]>('/affiliates-spark/kiosk/affiliates');
        return response.data;
    },
    fetchAffiliatePlans: async (): Promise<AffiliatePlan[]> => {
        const response = await axios.get<AffiliatePlan[]>('/affiliates-spark/kiosk/affiliates/plans');
        return response.data;
    },
    fetchSummary: async (): Promise<AffiliateSummary> => {
        const response = await axios.get<AffiliateSummary>('/api/affiliates/summary');
        return response.data;
    },
    fetchTransactions: async (page: number): Promise<Paginated<AffiliateTransaction>> => {
        const response = await axios.get<Paginated<AffiliateTransaction>>('/api/affiliates/transactions', { params: { page } });
        return response.data;
    },
    fetchBalance: async (): Promise<AffiliateBalance> => {
        const response = await axios.get<AffiliateBalance>('/api/affiliates/balance');
        return response.data;
    },
});
